import { IShapeOptions, BaseShape } from "./BaseShape";
import { Vector } from "../Core/Vector";

export class Line extends BaseShape {

    // 线段的起点
    private _start: Vector;
    // 线段的终点
    private _end: Vector;

    get start(): Vector { return this._start; }
    get end(): Vector { return this._end; }

    // 重载构造函数
    constructor();
    constructor(x1: number, y1: number, x2: number, y2: number, options?: IShapeOptions)
    constructor(start: Vector, end: Vector, options?: IShapeOptions)
    constructor(a?: any, b?: any, c?: any, d?: any, e?: any) {
        let opts: IShapeOptions | undefined;

        if (a === undefined && b === undefined) {
            // 无参数构造函数
            opts = undefined;
        } else if (a instanceof Vector && b instanceof Vector) {
            // 第二种调用方式，c 是 options
            opts = c as IShapeOptions;
        } else {
            // 第一种调用方式，e 是 options
            opts = e as IShapeOptions;
        }

        // 在设置 start/end 之前调用 super，确保 lineWidth 已初始化
        super(opts);

        if (a === undefined && b === undefined) {
            // 无参数情况下，创建默认线段（从原点到原点），
            // 由于两点重合，所以不会计算尺寸和绘制
            this._start = new Vector(0, 0);
            this._end = new Vector(0, 0);
        } else if (
            a instanceof Vector &&
            b instanceof Vector
        ) {
            this._start = a.clone();
            this._end = b.clone();
        } else if (
            typeof a === 'number' &&
            typeof b === 'number' &&
            typeof c === 'number' &&
            typeof d === 'number'
        ) {
            this._start = new Vector(a, b);
            this._end = new Vector(c, d);
        } else {
            throw new Error('Invalid arguments');
        }

        // 初始化时计算尺寸
        this.calcSize();
    }

    setStart(value: Vector) {
        this._start = value;
        this.calcSize();
        this.drawDirty = true; // 当属性变化时设置为需要重绘
    }

    setEnd(value: Vector) {
        this._end = value;
        this.calcSize();
        this.drawDirty = true;
    }

    setPoints(start: Vector, end: Vector) {
        this._start = start;
        this._end = end;
        this.calcSize();
        this.drawDirty = true;
    }

    protected arePointsCoincident(): boolean {
        return this._start.equals(this._end);
    }

    protected override draw(): void {
        // 如果起点和终点重合，则不需要绘制
        if (this.arePointsCoincident()) {
            this.drawDirty = false;
            return;
        }

        const ctx = this.target.ctx;

        // 清除之前的绘制内容
        ctx.clearRect(0, 0, this.width, this.height);
        // 开始新的路径以避免之前的路径状态干扰
        ctx.beginPath();

        // 获取线段的边界框左上角坐标（已考虑线宽）
        const { x: minX, y: minY } = this.calcPosition();
        // 计算绘制时的偏移量（半个线宽）
        const offset = this.lineWidth / 2;

        // 将线段起点和终点转换为相对于边界框左上角的坐标
        const startX = this._start.x - minX;
        const startY = this._start.y - minY;
        const endX = this._end.x - minX;
        const endY = this._end.y - minY;

        // 设置线宽
        ctx.lineWidth = this.lineWidth;
        // 设置线段颜色
        ctx.strokeStyle = this.lineColor;
        // // 设置线帽样式为 round 或 square 可以让端点更完整，默认为 butt
        ctx.lineCap = 'butt';

        // 设置线段起点
        ctx.moveTo(startX, startY);
        // 绘制线段
        ctx.lineTo(endX, endY);
        // 描边
        ctx.stroke();

        // 标记为不需要重绘
        this.drawDirty = false;
    }

    // 计算线段占据的区域的大小（考虑线宽）
    protected calcSize(): void {
        // 如果起点和终点重合，则不需要计算尺寸
        if (this.arePointsCoincident()) {
            this.setWidth(0);
            this.setHeight(0);
            this.target.setWidth(0);
            this.target.setHeight(0);
            return;
        }

        // 计算线段边界框的左上角（已考虑线宽）
        const { x: minX, y: minY } = this.calcPosition();

        // 计算基础宽度和高度
        const baseWidth = Math.abs(this._end.x - this._start.x);
        const baseHeight = Math.abs(this._end.y - this._start.y);

        // 考虑线宽，增加画布尺寸
        const width = baseWidth + this.lineWidth;
        const height = baseHeight + this.lineWidth;

        // 设置线段的宽度和高度
        this.setWidth(width);
        this.setHeight(height);

        // 设置canvas元素的宽度和高度
        this.target.setWidth(width);
        this.target.setHeight(height);

        // 移动线段到正确的位置
        this.moveTo(minX, minY);
    }

    // 计算线段边界框的左上角（即线段的 position）（考虑线宽）
    protected calcPosition(): Vector {
        // 计算不考虑线宽的边界
        const minXRaw = Math.min(this._start.x, this._end.x);
        const minYRaw = Math.min(this._start.y, this._end.y);

        // 向左上角偏移半个线宽
        const offset = this.lineWidth / 2;
        const minX = minXRaw - offset;
        const minY = minYRaw - offset;

        return new Vector(minX, minY);
    }
}